// Content List Page - Replaces ContentManagementPage
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConciergeListPage from '../../concierge/pages/ConciergeListPage';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';

import { useContents, useDeleteContent, useContentFiles } from '../hooks/useContent';
import { Content } from '../types';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedMuseum } from '../../../store/slices/museumSlice';

const ContentTable: React.FC<{
  contents: Content[];
  onView: (content: Content) => void;
  onEdit: (content: Content) => void;
  onDelete: (content: Content) => void;
}> = ({ contents, onView, onEdit, onDelete }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Waypoint</TableCell>
          <TableCell>Version</TableCell>
          <TableCell>Primary Text</TableCell>
          <TableCell>Duration</TableCell>
          <TableCell>Template</TableCell>
          <TableCell>Media</TableCell>
          <TableCell align="center">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {contents.map(content => (
          <TableRow key={`${content.waypoint_name}-${content.version}`} hover>
            <TableCell>
              <Typography variant="subtitle2">{content.waypoint_name}</Typography>
            </TableCell>
            <TableCell>
              <Chip label={`v${content.version}`} size="small" />
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ maxWidth: 200 }}>
                {content.primary_short_text_content}
              </Typography>
            </TableCell>
            <TableCell>{content.primary_duration}s</TableCell>
            <TableCell>
              <Chip label={content.primary_display_template} size="small" color="secondary" />
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {content.primary_image_url && <Chip label="📷" size="small" />}
                {content.primary_video_url && <Chip label="🎥" size="small" />}
                {content.primary_audio_url && <Chip label="🎵" size="small" />}
              </Box>
            </TableCell>
            <TableCell align="center">
              <Tooltip title="View">
                <IconButton size="small" onClick={() => onView(content)}>
                  <ViewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(content)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => onDelete(content)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ContentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMuseum = useAppSelector(selectSelectedMuseum);

  // Top-level service tab: "tour_guide" | "concierge"
  const activeServiceTab = searchParams.get('tab') ?? 'tour_guide';
  const setActiveServiceTab = (tab: string) => setSearchParams({ tab });

  // Museum tab for "All Museums" mode
  const [activeMuseumTab, setActiveMuseumTab] = useState('all');

  // Reset tab when museum selection changes
  useEffect(() => {
    setActiveMuseumTab('all');
  }, [selectedMuseum]);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'warning' | 'error'>('success');

  // Dialog states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);

  // Content - pass museum_code filter to API (undefined = all museums)
  const {
    data: contentTemplates,
    loading: contentLoading,
    refetch: refetchContent,
  } = useContents({ museumCode: selectedMuseum?.museum_code });

  // Delete content
  const deleteContent = useDeleteContent(msg => {
    setSnackbarMessage(msg);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  });

  // Content files
  const { data: files } = useContentFiles();

  const handleDeleteContent = async () => {
    if (!selectedContent) return;
    try {
      await deleteContent.mutate({
        waypointName: selectedContent.waypoint_name,
        version: selectedContent.version,
      });
      setSnackbarMessage('Content deleted successfully');
      setSnackbarSeverity('success');
      await refetchContent();
      setOpenDeleteDialog(false);
      setSelectedContent(null);
      setSnackbarOpen(true);
    } catch {
      // Error message will be set by mutation error handler
    }
  };

  const handleView = (content: Content) =>
    navigate(`/content/${encodeURIComponent(content.waypoint_name)}/${content.version}`);

  const handleEdit = (content: Content) =>
    navigate(`/content/${encodeURIComponent(content.waypoint_name)}/${content.version}/edit`);

  const handleDeleteClick = (content: Content) => {
    setSelectedContent(content);
    setOpenDeleteDialog(true);
  };

  const filteredContent = contentTemplates || [];

  // Group content by museum when All Museums is selected
  const groupedContent = !selectedMuseum
    ? filteredContent.reduce<Record<string, Content[]>>((acc, content) => {
        const key = content.museum_code || (content.museum != null ? String(content.museum) : null) || 'Unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(content);
        return acc;
      }, {})
    : null;

  const museumGroups = groupedContent ? Object.entries(groupedContent).sort(([a], [b]) => a.localeCompare(b)) : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4">Content Management</Typography>
          {selectedMuseum && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              {selectedMuseum.name}
            </Typography>
          )}
          {!selectedMuseum && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              All Museums
            </Typography>
          )}
        </Box>
        {activeServiceTab === 'tour_guide' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/content/create')}
              disabled={!selectedMuseum}
            >
              Create Content
            </Button>
          </Box>
        )}
      </Box>

      {/* Service tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeServiceTab} onChange={(_e, val) => setActiveServiceTab(val)}>
          <Tab label="Tour Guide" value="tour_guide" />
          <Tab label="Concierge" value="concierge" />
        </Tabs>
      </Box>

      {/* Concierge tab */}
      {activeServiceTab === 'concierge' && <ConciergeListPage />}

      {/* Tour Guide tab */}
      {activeServiceTab === 'tour_guide' && (
        <>
          {contentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedMuseum ? (
            /* Single museum view */
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Content ({filteredContent.length})
                </Typography>
                {filteredContent.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No content found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create your first content for {selectedMuseum.name}
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/content/create')}>
                      Create Content
                    </Button>
                  </Box>
                ) : (
                  <ContentTable
                    contents={filteredContent}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            /* All Museums view - tabs to toggle between museums */
            <>
              {museumGroups && museumGroups.length === 0 ? (
                <Card>
                  <CardContent>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        No content found across any museum
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select a museum from the sidebar to create content.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Museum toggle tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                      value={activeMuseumTab}
                      onChange={(_e, val) => setActiveMuseumTab(val)}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label="All" value="all" />
                      {museumGroups &&
                        museumGroups.map(([museumCode, contents]) => (
                          <Tab
                            key={museumCode}
                            label={`${contents[0]?.museum_name || museumCode} (${contents.length})`}
                            value={museumCode}
                          />
                        ))}
                    </Tabs>
                  </Box>

                  {museumGroups &&
                    museumGroups
                      .filter(([museumCode]) => activeMuseumTab === 'all' || museumCode === activeMuseumTab)
                      .map(([museumCode, contents], index) => (
                        <Box key={museumCode}>
                          {index > 0 && <Divider sx={{ my: 3 }} />}
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {contents[0]?.museum_name || museumCode} — Content ({contents.length})
                              </Typography>
                              <ContentTable
                                contents={contents}
                                onView={handleView}
                                onEdit={handleEdit}
                                onDelete={handleDeleteClick}
                              />
                            </CardContent>
                          </Card>
                        </Box>
                      ))}
                </>
              )}
            </>
          )}

          {/* File Management Section - TODO: Move to separate page later */}
          {files && files.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Uploaded Files ({files.length})
                </Typography>
                {/* File list implementation here */}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the content for &quot;{selectedContent?.waypoint_name}&quot; (version{' '}
            {selectedContent?.version})? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteContent} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentListPage;
